/*
  FN: 16/12/2019
  v-f-date-picker complexity explained

  This component has got quite complex for a few different reasons.
  1. The Date Picker we use only works with vanilla js Date()
  2. The requirements mean we need to accept null value dates and
  display them as 'DD/MM/YYYY' for date and 'HH:MM' for time. This
  is complicated as they both need to share the same date. However
  they both need to work independently. ie, if a user sets a date,
  the date part of the the date picker should update. However the
  time should still display as 'HH:MM'. As this isn't enough data
  to create a valid date however, we have to use a complicated life
  cycle. In short this means this component needs to store it's own
  version of the date and time before updating it in formik.

  The life cycle works like so:

  1. v-f-date-picker has it's own state related to the date (saved as
  an ISO date string under the key 'selectedDate'. It also has it's
  own state for time, saved in two keys, hh for hours, mm for minutes.

  2. On Instantiation the selected date, hours and minutes are set for
  the given initial value (which is either an ISO date string or null).
  If the ISO date string is null then `selectedDate: null`, `hh: 'HH'`
  and `mm: 'MM'`.

  3. When update value is called, it is called either with a new ISO
  string date (if from the date picker) or hh/mm for time picker.
  The component will then consider updating it's calue in formik. To
  do this it tests if both hh and mm are equivelent to numbers (ie,
  '00' as oposed 'HH' or 'H0'). If they both are, and selectedDate has
  a value, it will update the value in formik as an ISO date string.
  Otherwise it'll just update the local state of the component as it
  doesn't have enough data to create a valid ISO date string.
*/



import React, { PureComponent } from 'react';
// https://reactdatepicker.com/#example-exclude-dates
// https://github.com/Hacker0x01/react-datepicker/
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {
  CalenderSvg,
} from 'AppSrc/core/components/common/svg-icons';
import { getTimezoneForNetwork } from 'AppSrc/core/utility-functions/date-functions';
import { DatePickerModal } from './sub-components/date-picker-modal';
import { InlineErrors } from './sub-components/inline-errors';
import { FieldTitle } from './sub-components/field-title';
import { TimeInput } from './sub-components/time-input';
import {
  vFParamsPropShape,
  disabledFieldOptionsShape,
} from './data-structure/v-f-proptypes';
import {
  getFieldPropShape,
  formPropShape,
} from './data-structure/formik-proptypes';
import 'AppSrc/core/style/components/form/form-base-components/v-f-date-picker.css';

// regex for checking if a value of any type is a number. For example:
// '01' ✅
// 1 ✅
// 'hh' ❌
// 'h1' ❌
const isNumeric = (value) => /^-{0,1}\d+$/.test(value);

class VFDatePicker extends PureComponent {
  constructor(props) {
    super(props);

    const value = props.field.value;
    const timezone = this.getTimezone();
    const initialMoment = value ? moment.tz(value, timezone) : null;
    const initialhh = initialMoment ? initialMoment.format('HH') : 'HH';
    const initialmm = initialMoment ? initialMoment.format('mm') : 'MM';

    this.state = {
      selectedDate: value,
      modalOpened: false,
      hh: initialhh,
      mm: initialmm,
    };
    this.toggleModal = this.toggleModal.bind(this);
    this.updateValue = this.updateValue.bind(this);
    this.refocusDateBtn = this.refocusDateBtn.bind(this);
    this.selectedDateBtnRef = React.createRef();
    this.getTimezone = this.getTimezone.bind(this);

  }

  componentDidMount() {
    this.ifDisabledSetDefaultValues({});
  }

  componentDidUpdate(prevProps) {
    this.ifDisabledSetDefaultValues({
      prevProps,
    });
  }

  getTimezone() {
    const { selectedNetwork } = this.props.vFParams;
    return getTimezoneForNetwork(selectedNetwork);
  }

  // after closing the modal, we need to refocus the selected date
  // button explicitly in for IE11
  refocusDateBtn() {
    this.selectedDateBtnRef.current.focus();
  }

  toggleModal() {
    this.setState(
      (prevState) => ({
        modalOpened: !prevState.modalOpened,
      }),
      () => {
        const { modalOpened } = this.state;
        // if we just closed the modal, we have to refocus the date button
        if (!modalOpened) {
          this.refocusDateBtn();
        }
      },
    );
  }

  updateValue(params) {
    const {
      selectedDate,
      hh,
      mm,
    } = params;
    const {
      field,
    } = this.props;
    if ((hh !== undefined) && (mm !== undefined)) {
      this.setState({
        hh,
        mm,
      });
    }
    if (selectedDate !== undefined) {
      this.setState({
        selectedDate,
      });
    }

    const hhToUse = hh ? hh : this.state.hh;
    const mmToUse = mm ? mm : this.state.mm;
    const selectedDateToUse = selectedDate ? selectedDate : this.state.selectedDate;


    const {
      name,
    } = field;
    const modalStartedOpen = this.state.modalOpened;
    this.setState(
      {
        modalOpened: false,
      },
      () => {
        if (modalStartedOpen) {
          // refocus date button after closing the modal
          this.refocusDateBtn();
        }
      },
    );
    const timezone = this.getTimezone();
    const dateMoment = moment.tz(selectedDateToUse, timezone);



    const dateConstructed = isNumeric(hhToUse) && isNumeric(mmToUse) && selectedDateToUse;


    if (dateConstructed) {
      dateMoment.set({
        hour: hhToUse,
        minute: mmToUse,
      });
      const valueToSet = dateMoment.toISOString();
      // this has to be an ISOstring as redux can't handle dates
      this.props.form.setFieldValue(name, valueToSet);
      this.props.form.setFieldTouched(name, true);
    }
  }


  ifDisabledSetDefaultValues(params) {
    const {
      prevProps,
    } = params;
    const {
      name,
      value,
    } = this.props.field;
    const {
      disabled,
      disabledValue,
      unDisabledValue,
    } = this.props.disabledFieldOptions;
    // check if the previous props had set it to disabled
    const prevDisabled = prevProps ? prevProps.disabledFieldOptions.disabled : false;

    if (!disabled) {
      if (prevDisabled) {
        this.props.form.setFieldValue(name, unDisabledValue);
        this.props.form.setFieldTouched(name, true);
      }
      return null;
    }

    if (value !== disabledValue) {
      this.props.form.setFieldValue(name, disabledValue);
      this.props.form.setFieldTouched(name, true);
    }

    return null;
  }


  render() {

    const {
      vFParams,
      field,
      form,
      disabledFieldOptions,
      optional,
      readOnly,
    } = this.props;


    const {
      modalOpened,
      selectedDate,
      hh,
      mm,
    } = this.state;

    const {
      name,
    } = field;

    const {
      disabled,
    } = disabledFieldOptions;

    const dateObj = selectedDate ? new Date(selectedDate) : null;

    const formErrors = form.errors;
    const touched = form.touched[name];


    const errors = formErrors[name];

    const inlineErrors = errors && errors.inlineErrors ? errors.inlineErrors : null;

    const {
      fieldTitle,
      displayType,
      minDate,
      maxDate,
      excludeDates,
    } = vFParams;

    const timezone = this.getTimezone();
    const displayMoment = dateObj ? moment.tz(dateObj, timezone) : null;
    const displayValue = displayMoment ? displayMoment.format('DD/MM/YYYY') : 'DD/MM/YYYY';
    const timezoneName = displayMoment ? displayMoment.format('z') : '';
    const helperText = timezoneName ? `(${timezoneName} 24hr)` : null;


    // We don't display the field title if the display is 'big'
    // However we still need to provide a field title for the summary page
    const displayFieldTitle = fieldTitle && displayType !== 'big';

    // unforunatley default props doesn't work with nested data so we have to state a default here
    const displayClassName = displayType ? displayType : 'standard';

    const className = `v-f-field-type v-f-date-picker-${displayClassName} v-f-date-picker${disabled ? ' v-f-date-picker-disabled' : ''} ${readOnly ? ' v-f-input-read-only' : ''} v-f-date-picker-${name}`;
    return (
      <div
        className={className}
        name={name}
      >
        {
          displayFieldTitle && (
            <FieldTitle
              fieldTitle={fieldTitle}
              optional={optional}
            />
          )
        }
        <button
          ref={this.selectedDateBtnRef}
          type="button"
          onClick={this.toggleModal}
          disabled={disabled}
          // for now this is just a modal but in future there is to be a none modal version as well
          className="selected-date selected-date-modal-button"
        >
          <div className="selected-date-inner">
            <p className="selected-date-text">
              {displayValue}
            </p>
            <div className="calender-svg-wrapper">
              <CalenderSvg />
            </div>
          </div>
        </button>
        {
          modalOpened && (
            <DatePickerModal
              savedSelectedDate={dateObj}
              excludeDates={excludeDates}
              minDate={minDate}
              maxDate={maxDate}
              toggleModal={this.toggleModal}
              updateValue={this.updateValue}
            />
          )
        }
        <div className="date-time-seperator">
          /
        </div>
        <div className="selected-time-wrapper">
          <TimeInput
            hh={hh}
            mm={mm}
            updateValue={this.updateValue}
            readOnly={readOnly}
          />
          <div className="time-helper-text">
            {helperText}
          </div>
        </div>
        {
          inlineErrors && touched && (
            <InlineErrors
              inlineErrors={inlineErrors}
            />
          )
        }
      </div>
    );
  }
}


VFDatePicker.propTypes = {
  vFParams: vFParamsPropShape,
  // we have to define the value within the data structure for field
  field: getFieldPropShape(PropTypes.string),
  form: formPropShape,
  disabledFieldOptions: disabledFieldOptionsShape,
  optional: PropTypes.bool,
  readOnly: PropTypes.bool,
};



const noValueDatePicker = (value) => value === null;
// This is a reference to the component. This is needed as the value for each form field is displayed on a change summary
// page but this is dependant on what type of form field is used
const VF_DATE_PICKER = 'v-f-date-picker';

export {
  VFDatePicker,
  noValueDatePicker,
  VF_DATE_PICKER,
};
